import * as ChapterType from "utils/ChapterType";
import * as ImageSize from "utils/ImageSize";
import classnames from "classnames";
import FocalPoint from "components/FocalPoint";
import getParentChapterSlug from "selectors/chapter/getParentChapterSlug";
import Image from "components/Image";
import makeGetChapterEntity from "selectors/chapter/makeGetChapterEntity";
import PropTypes from "prop-types";
import React from "react";
import { autobind } from "core-decorators";
import { connect } from "react-redux";
import keycode from "keycode";
import styles from "./ChapterListItemStyles.css";

/**
 * Convert seconds to 0:00 format
 *
 * @param {number} seconds Duration in seconds
 */
function formatDuration(seconds) {
  return `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0")}`;
}
class ChapterListItem extends React.PureComponent {
  static propTypes = {
    chapterId: PropTypes.number.isRequired,
    duration: PropTypes.number.isRequired,
    handleItemClick: PropTypes.func,
    isSubchapter: PropTypes.bool,
    showChapterTimeInChapterList: PropTypes.bool.isRequired,
    thumbnail: PropTypes.object,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    typeDisplay: PropTypes.string.isRequired,
  };

  state = {
    hover: false,
  };

  @autobind
  setHover() {
    this.setState({
      hover: true,
    });
  }

  @autobind
  unsetHover() {
    this.setState({
      hover: false,
    });
  }

  render() {
    const thumbnailContainer =
      this.props.thumbnail && !this.props.isSubchapter ? (
        <div className="vertical-modal-list-item-thumbnail">
          <FocalPoint
            containThreshold={Infinity}
            fitMode="cover"
            focalPoint={this.props.thumbnail}
            className="vertical-modal-list-item-thumbnail__image"
          >
            <Image size={ImageSize.MEDIUM} src={this.props.thumbnail.src} />
          </FocalPoint>
        </div>
      ) : null;
    return (
      <div
        {...{
          [this.props.isSubchapter ? "data-subchapter" : "data-parentchapter"]:
            "",
        }}
        data-chapter-type={this.props.type}
        aria-label={this.props.title}
        className="vertical-modal-list-item"
        onClick={this.props.handleItemClick}
        onMouseEnter={this.setHover}
        onMouseLeave={this.unsetHover}
        onTouchEnd={this.unsetHover}
        onTouchStart={this.setHover}
        onKeyUp={(event) => {
          switch (keycode(event).toLowerCase()) {
            case "enter":
            case "space":
              this.props.handleItemClick?.(event);
          }
        }}
        ref="target"
        role="button"
        tabIndex={0}
      >
        <div
          className={`${styles.chapterContent} vertical-modal-list-item-content`}
        >
          {thumbnailContainer}
          <div
            className={`${styles.chapterCopy} vertical-modal-list-item-copy`}
          >
            <div className={classnames(styles.title)}>
              <span
                className={
                  this.props.isSubchapter
                    ? "font-size--xsmall--scaling"
                    : "font-style--header-2 font-size--medium--scaling"
                }
              >
                {this.props.title}
              </span>
            </div>
          </div>
          {this.props.showChapterTimeInChapterList &&
            typeof this.props.duration === "number" &&
            this.props.duration > 0 && (
              <div
                className={`${styles.chapterLabels} vertical-modal-list-item-labels font-size--xsmall--scaling`}
              >
                <div data-chapter-timing className={styles.time}>
                  {formatDuration(this.props.duration / 1000)}
                </div>
              </div>
            )}
        </div>
      </div>
    );
  }
}
const makeMapStateToProps = () => {
  const getChapterEntity = makeGetChapterEntity();
  return (state, ownProps) => {
    const chapterEntity = getChapterEntity(state, ownProps);

    const parentChapterSlug = getParentChapterSlug(state, ownProps);
    return {
      duration: chapterEntity.duration,
      handleItemClick: () =>
        ownProps.handleItemClick({
          chapterId: ownProps.chapterId,
          chapterKey: parentChapterSlug || chapterEntity.slugs[0],
          chapterTitle: chapterEntity.title,
          routeName: parentChapterSlug ? "subchapter" : "chapter",
          subchapterKey: parentChapterSlug ? chapterEntity.slugs[0] : null,
        }),
      isSubchapter: !!chapterEntity.parent,
      showChapterTimeInChapterList:
        state.playerOptions.showChapterTimeInChapterList,
      thumbnail:
        chapterEntity.thumbnail &&
        state.story.entities.images[chapterEntity.thumbnail],
      title: chapterEntity.title,
      type: chapterEntity.type,
      typeDisplay:
        chapterEntity.type === ChapterType.INTERVIEW
          ? "Explainer"
          : chapterEntity.type,
    };
  };
};

export default connect(makeMapStateToProps)(ChapterListItem);
